/**
 * Audio analysis utilities for non-speech detection
 * Helps prevent sending silent/noise audio frames to OpenAI
 */

export interface AudioFrameAnalysis {
  rms: number; // Root Mean Square amplitude
  zcr: number; // Zero Crossing Rate
  energy: number; // Total frame energy
}

export interface SpeechThresholds {
  rms: number; // RMS threshold for speech detection
  energy: number; // Energy threshold for speech detection
  zcr: number; // Zero crossing rate threshold for speech detection
}

/**
 * Default thresholds optimized for medical dictation in noisier environments
 * These can be adjusted based on microphone sensitivity and room noise
 */
export const DEFAULT_SPEECH_THRESHOLDS: SpeechThresholds = {
  rms: 0.01, // Minimum amplitude for actual speech
  energy: 5.0, // Higher energy threshold to filter out background noise
  zcr: 0.15, // Much higher ZCR threshold - background noise has high ZCR but low energy
};

/**
 * Analyze an audio frame to compute RMS amplitude, Zero Crossing Rate, and energy
 *
 * @param frame - Float32Array of audio samples
 * @returns Analysis results including RMS, ZCR, and energy
 */
export function analyzeAudioFrame(frame: Float32Array): AudioFrameAnalysis {
  let rmsSum = 0;
  let energy = 0;
  let zeroCrossings = 0;

  for (let i = 0; i < frame.length; i++) {
    const sample = frame[i];

    // Calculate RMS (Root Mean Square)
    rmsSum += sample * sample;

    // Calculate total energy
    energy += Math.abs(sample);

    // Count zero crossings (sign changes between adjacent samples)
    if (i > 0 && Math.sign(frame[i]) !== Math.sign(frame[i - 1])) {
      zeroCrossings++;
    }
  }

  const rms = Math.sqrt(rmsSum / frame.length);
  const zcr = zeroCrossings / frame.length;

  return { rms, zcr, energy };
}

/**
 * Voice activity for utterance segmentation on the streaming surface.
 *
 * Deliberately RMS-only. `energy` is a raw sum over the frame — never divided
 * by length — so its threshold is implicitly calibrated to one frame size:
 * 5.0 was tuned against 2048-sample frames, and on the streaming path's
 * 1024-sample frames the same voice produces half the sum, gating quiet
 * speech out entirely. When that happens no utterance ever registers, the
 * silence timer never arms, and dictation stops segmenting mid-speech. RMS is
 * normalized per sample, so it means the same thing at any frame size.
 *
 * `isSpeechFrame` (the compound gate) remains what it always was: the
 * noise filter for Assessment's outbound audio, at Assessment's frame size.
 */
export function hasVoiceActivity(
  analysis: AudioFrameAnalysis,
  thresholds: SpeechThresholds = DEFAULT_SPEECH_THRESHOLDS,
): boolean {
  return analysis.rms > thresholds.rms;
}

/**
 * Determine if an audio frame contains speech based on analysis metrics
 *
 * @param analysis - Frame analysis results
 * @param thresholds - Speech detection thresholds
 * @returns true if frame likely contains speech, false for silence/noise
 */
export function isSpeechFrame(
  analysis: AudioFrameAnalysis,
  thresholds: SpeechThresholds = DEFAULT_SPEECH_THRESHOLDS,
): boolean {
  const { rms, zcr, energy } = analysis;

  // Check multiple indicators of speech:
  const rmsSpeech = rms > thresholds.rms; // Amplitude above background noise
  const energySpeech = energy > thresholds.energy; // Total energy above HVAC/equipment noise
  const zcrSpeech = zcr > thresholds.zcr; // Voice frequency content vs background

  // Require BOTH energy AND either RMS or ZCR to be above threshold
  // This prevents background noise with high ZCR but low energy from passing through
  return energySpeech && (rmsSpeech || zcrSpeech);
}

/**
 * Adaptive threshold adjustment based on background noise
 * Call this during quiet periods to calibrate thresholds
 *
 * @param baselineFrames - Array of analysis results from quiet/baseline audio
 * @returns Adjusted thresholds based on noise floor
 */
export function calibrateThresholds(
  baselineFrames: AudioFrameAnalysis[],
): SpeechThresholds {
  if (baselineFrames.length === 0) {
    return DEFAULT_SPEECH_THRESHOLDS;
  }

  // Calculate average noise floor
  const avgRms =
    baselineFrames.reduce((sum, frame) => sum + frame.rms, 0) /
    baselineFrames.length;
  const avgEnergy =
    baselineFrames.reduce((sum, frame) => sum + frame.energy, 0) /
    baselineFrames.length;
  const avgZcr =
    baselineFrames.reduce((sum, frame) => sum + frame.zcr, 0) /
    baselineFrames.length;

  // Set thresholds above noise floor with safety margin
  return {
    rms: Math.max(avgRms * 3, DEFAULT_SPEECH_THRESHOLDS.rms),
    energy: Math.max(avgEnergy * 3, DEFAULT_SPEECH_THRESHOLDS.energy),
    zcr: Math.max(avgZcr * 2, DEFAULT_SPEECH_THRESHOLDS.zcr),
  };
}
